import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const SidebarPermissionsContext = createContext();

export function useSidebarPermissions() {
  const context = useContext(SidebarPermissionsContext);
  if (!context) {
    throw new Error('useSidebarPermissions deve ser usado dentro de um SidebarPermissionsProvider');
  }
  return context;
}

export function SidebarPermissionsProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Carregar permissões quando o usuário mudar
  useEffect(() => {
    if (user) {
      loadPermissions();
    } else {
      setPermissions({});
      setLoading(false);
    }
  }, [user]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      console.log('🔄 Carregando permissões para usuário:', {
        username: user?.username,
        role: user?.role,
        companyId: user?.companyId
      });
      
      // Se não houver usuário ou empresa, usar permissões padrão
      if (!user || !user.companyId) {
        console.log('⚠️ Usuário sem empresa, usando permissões padrão');
        setDefaultPermissions();
        return;
      }

      // Tentar carregar permissões do backend
      try {
              console.log(`📡 Fazendo chamada para sidebar-permissions/${user.companyId}`);
      const response = await api.get(`/sidebar-permissions/${user.companyId}`);
        console.log('📥 Resposta do backend:', response.data);
        
        if (response.data.success) {
          console.log('✅ Permissões carregadas do backend:', response.data.permissions);
          setPermissions(response.data.permissions);
        } else {
          console.log('⚠️ Backend retornou erro, usando permissões padrão');
          setDefaultPermissions();
        }
      } catch (error) {
        console.log('❌ Erro ao carregar permissões do backend:', error);
        console.log('🔄 Usando permissões padrão');
        setDefaultPermissions();
      }
    } catch (error) {
      console.error('❌ Erro geral ao carregar permissões:', error);
      setDefaultPermissions();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultPermissions = () => {
    // Permissões padrão baseadas no role do usuário
    const defaultPermissions = {
      dashboard: ['admin', 'manager', 'common_user', 'viewer'],
      profile: ['admin', 'manager', 'common_user', 'viewer'],
      clients: ['admin', 'manager', 'common_user', 'viewer'],
      settings: ['admin']
    };
    
    console.log('🔄 Definindo permissões padrão:', {
      userRole: user?.role,
      defaultPermissions: defaultPermissions
    });
    
    setPermissions(defaultPermissions);
  };

  const hasPermission = (itemKey) => {
    if (user?.role === 'admin') return true;

    if (!user || !permissions[itemKey]) {
      return false;
    }
    
    return permissions[itemKey].includes(user.role);
  };

  const updatePermissions = async (newPermissions) => {
    try {
      if (!user?.companyId) {
        throw new Error('Usuário não tem empresa associada');
      }

      console.log('📤 Enviando permissões para o backend:', newPermissions);

      const response = await api.post('/sidebar-permissions', {
        permissions: newPermissions
      });

      if (response.data.success) {
        console.log('✅ Permissões salvas com sucesso no backend');
        // Atualizar permissões locais com as novas permissões
        setPermissions(newPermissions);
        return { success: true };
      } else {
        throw new Error(response.data.error || 'Erro ao salvar permissões');
      }
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    permissions,
    loading,
    hasPermission,
    updatePermissions,
    loadPermissions
  };

  return (
    <SidebarPermissionsContext.Provider value={value}>
      {children}
    </SidebarPermissionsContext.Provider>
  );
}
